import { useState, useCallback } from "react";
import { Download, Calendar } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../../lib/api/config";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useTranslation } from "../../context/TranslationContext";
import { ModalWrapper, ModalHeader, ModalBody, ModalFooter, FormField, FormSelect, Button, InfoBox } from "../common";

interface ExportModalProps {
  userId: string;
  site: string;
  onClose: () => void;
}

export function ExportModal({ userId, site, onClose }: ExportModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exportType, setExportType] = useState<"all" | "user">("all");
  const [isExporting, setIsExporting] = useState(false);
  const [holidays] = useState([]);

  const isPublicHoliday = useCallback((date: Date | string): boolean => {
    return false;
  }, [holidays]);

  const handleExport = async () => {
    if (!token) return;

    setIsExporting(true);

    let url = `${API_URL}/export/${site}`;
    if (exportType === "user") {
      url += `/${userId}`;
    }

    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`;
    }

    // Close the modal immediately
    onClose();

    // Show download progress toast
    const toastId = toast.loading(t('export.preparingExport'), {
      duration: Infinity, // Keep it until we manually dismiss it
    });

    try {
      // Fetch the file
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('export.failedToExport'));
      }

      // Get the blob
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      // Generate filename
      const timestamp = format(new Date(), "yyyy-MM-dd");
      const filename = exportType === "all" 
        ? `events-${site}-${timestamp}.csv`
        : `events-${site}-${userId}-${timestamp}.csv`;
      
      link.setAttribute("download", filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(downloadUrl);

      // Show success message
      toast.success(t('export.exportDownloaded'), { 
        id: toastId,
        duration: 3000 
      });

    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error(t('export.failedToExport'), { 
        id: toastId,
        duration: 4000 
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ModalWrapper data-tsx-id="export-modal" onClose={onClose} size="md">
      <ModalHeader
        icon={<Calendar />}
        title={t('export.exportEvents')}
        onClose={onClose}
      />

      <ModalBody>
        <FormField label={t('export.exportType')}>
          <FormSelect
            options={[
              { value: "all", label: t('export.allEvents') },
              { value: "user", label: t('export.myEventsOnly') },
            ]}
            value={exportType}
            onChange={(e) => setExportType(e.target.value as "all" | "user")}
            disabled={isExporting}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('export.startDateOptional')}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
              disabled={isExporting}
            />
          </FormField>

          <FormField label={t('export.endDateOptional')}>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
              disabled={isExporting}
            />
          </FormField>
        </div>

        <InfoBox variant="info" className="text-sm">
          {t('export.leaveDatesEmpty')}
        </InfoBox>
      </ModalBody>

      <ModalFooter alignment="right">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isExporting}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={isExporting}
          loading={isExporting}
          icon={<Download />}
        >
          {t('common.export')}
        </Button>
      </ModalFooter>
    </ModalWrapper>
  );
}